// Commission = what the CANDIDATE pays ADEL (owner 2026-08-17: the Kenyan term for what other
// markets call a service charge). The server sends { amount, currency, coversAll } ONLY when the
// demand genuinely charges the candidate — it withholds the whole object when the client pays, when
// no currency is stored, when the amount is 0, or when the admin has the field hidden. So a null
// here means "show no commission line", never "free", and never "we don't know".
//
// No currency fallback on purpose: the server guarantees one (it fails closed rather than guessing,
// because defaulting a QAR charge to KES understates it ~35x). If currency were ever missing we want
// the bug visible, not papered over with a wrong-but-plausible number.
// Currency-first to match salaryKesText's "≈ KES 45,000" line it sits beneath.
export function commissionText(commission) {
  if (!commission || commission.amount == null || !commission.currency) return null;
  return `${commission.currency} ${Number(commission.amount).toLocaleString('en-KE')}`;
}

// Shown ONLY when the server says coversAll — i.e. this demand provides the joining ticket AND the
// candidate is not merely splitting the cost with the client. Never render it unconditionally: on a
// 'Not Provided' ticket demand this sentence would be a false commercial promise on a public page.
export const COMMISSION_COVERS_ALL_TEXT =
  'Covers all recruitment charges, including your air ticket. Nothing else to pay.';

// Shared with JobCard.js and JobDetailPage.js so the format/fallback rule can't drift between them.
export function salaryText(salary) {
  if (!salary) return 'Competitive salary';
  if (salary.type === 'range') {
    return [salary.from, salary.to].filter((v) => v != null).join('–') + (salary.currency ? ` ${salary.currency}` : '');
  }
  return salary.amount != null ? `${salary.amount}${salary.currency ? ' ' + salary.currency : ''}` : 'Competitive salary';
}

// Converted-salary keys, in preference order. Kenya first — see salaryKesText below.
const CONVERTED_SALARY = [
  { code: 'KES', locale: 'en-KE', from: 'kesFrom', to: 'kesTo', amount: 'kesAmount' },
  { code: 'INR', locale: 'en-IN', from: 'inrFrom', to: 'inrTo', amount: 'inrAmount' },
];

export function salaryKesText(salary) {
  if (!salary) return null;
  // The ERP names this figure after the INSTANCE's base currency: Kenya emits kesAmount/kesFrom/
  // kesTo, India emits inrAmount/inrFrom/inrTo (backend utils/publicJobProjection.ts SALARY_KEY).
  // Already rounded to the nearest 1,000 server-side; this only adds separators and the range dash.
  // Kenya is checked FIRST so its output is byte-identical to before. Reading both keeps this portal
  // working if it is ever pointed at the India backend instead of silently showing no figure at all.
  for (const c of CONVERTED_SALARY) {
    const fmt = (n) => n.toLocaleString(c.locale);
    if (salary.type === 'range') {
      const parts = [salary[c.from], salary[c.to]].filter((v) => v != null);
      if (parts.length > 0) return `≈ ${c.code} ${parts.map(fmt).join(' – ')}`;
    } else if (salary[c.amount] != null) {
      return `≈ ${c.code} ${fmt(salary[c.amount])}`;
    }
  }
  return null;
}
